<template>
    <form
        data-test-id="upload-diagnostic-log-form"
        @submit.prevent="submit"
    >
        <div class="form-group">
            <file-upload
                ref="fileUpload"
                :url="`${organizationSlug}/diagnostic-logs`"
                accept=".zip,.log"
                label="Log File"
                data-test-id="diagnostic-log-file-upload"
                :required="true"
                @error="handleUploadError"
                @success="tempUploadComplete"
                @upload-started="uploadStarted"
            />
        </div>

        <div class="form-group">
            <label :for="`diagnostic_log_description_${uid}`">Description</label>
            <textarea
                :id="`diagnostic_log_description_${uid}`"
                v-model="description"
                data-test-id="diagnostic-log-description-field"
                maxlength="191"
            />
        </div>

        <form-errors :response="errorResponse" />

        <div class="form-group form-group--actions">
            <button
                type="button"
                class="btn"
                data-test-id="cancel-diagnostic-log-upload-button"
                :disabled="submitted"
                @click="requestClose"
            >
                Cancel
            </button>

            <button
                type="submit"
                class="btn btn-primary"
                :class="{ active: submitted }"
                data-test-id="submit-diagnostic-log-upload-button"
            >
                <div>Upload</div>
            </button>
        </div>
    </form>
</template>

<script>
import { getCurrentInstance } from 'vue'
import canConfirm from '@/mixins/canConfirm'
import FileUpload from '@/components/FileUpload/index.vue'
import FormErrors from '@/components/FormErrors.vue'
import needsOrganizationSlug from '@/mixins/needsOrganizationSlug'

export default {
    name: 'UploadDiagnosticLogForm',

    components: {
        FileUpload,
        FormErrors,
    },

    mixins: [canConfirm, needsOrganizationSlug],

    emits: ['cancel', 'uploaded'],

    data() {
        return {
            description: null,
            errorResponse: null,
            filenames: null,
            submitted: false,
        }
    },

    computed: {
        uid() {
            return getCurrentInstance().uid
        },

        fileUploaded() {
            return Boolean(this.filenames?.original_filename && this.filenames?.temp_filename)
        },

        // filenames is set as soon as a file is chosen, so this covers a file
        // that is still uploading as well as one that has finished
        hasEnteredInformation() {
            return this.filenames !== null || Boolean(this.description?.trim())
        },
    },

    methods: {
        // when the upload is started, add a placeholder so the submit
        // handler knows a file is on its way up
        uploadStarted() {
            this.filenames = {}
        },

        tempUploadComplete({ original, temp }) {
            this.filenames = {
                original_filename: original,
                temp_filename: temp,
            }

            this.errorResponse = null
        },

        handleUploadError(formErrorResponse) {
            this.errorResponse = formErrorResponse
            this.filenames = null
        },

        // Entry point for every way of dismissing the modal: the cancel button,
        // the dialog's close button, and the escape key.
        async requestClose() {
            // never interrupt a submit that is already in flight
            if (this.submitted) {
                return
            }

            if (!this.hasEnteredInformation) {
                this.discardAndClose()
                return
            }

            const discard = await this.confirm({
                message: 'Are you sure you want to cancel adding this diagnostic log?',
                acceptLabel: 'Discard',
                rejectLabel: 'Keep Editing',
                danger: true,
            })

            if (discard) {
                this.discardAndClose()
            }
        },

        discardAndClose() {
            this.$refs.fileUpload.abort()
            this.$emit('cancel')
        },

        submit() {
            if (this.submitted) {
                return
            }

            if (!this.fileUploaded) {
                this.errorResponse = {
                    data: {
                        errors: {
                            Files: ['Please wait until all files have been uploaded.'],
                        },
                    },
                }
                return
            }

            this.submitted = true

            this.$http
                .post(`${this.organizationSlug}/diagnostic-logs`, {
                    original_filename: this.filenames.original_filename,
                    temp_filename: this.filenames.temp_filename,
                    description: this.description,
                })
                .then(() => {
                    this.$emit('uploaded')
                })
                .catch((error) => {
                    this.errorResponse = error.response
                    this.submitted = false
                })
        },
    },
}
</script>

<style lang="scss" scoped>
.form-group--actions {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    margin-bottom: 0;
}
</style>
